import '../../index.css';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  IconButton,
  useTheme,
  Typography,
  Snackbar,
  Alert,
  useMediaQuery,
  TextField,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/pagination';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import CloseIcon from '@mui/icons-material/Close';
import i18nStore from '../../stores/i18nStore';
import objectStore from '../../stores/objectStore';
import RenderSelectedTags from '../settings/tags/RenderSelectedTags';

import DynamicFieldsEdit from '../DynamicFieldsEdit';
import { config } from '../../config';
import logger from '../../utils/logger';
import { getPrice } from '../../utils/priceUtils';
import { getObjectFullInfo } from '../../utils/objectInfoUtils';
import { LuTrash2 } from 'react-icons/lu';
import { IoClose } from 'react-icons/io5';
import { AiOutlineEdit } from 'react-icons/ai';
import { IoShareSocialOutline } from 'react-icons/io5';
import HideImageIcon from '@mui/icons-material/HideImage';
import AvitoPreviewDialog from './AvitoPreviewDialog';
import ObjectDistributionControls from './ObjectDistributionControls';
import { scopedObjectComments } from '../../tenancy/scopedObjectDto';
import { canDistributeObject, canMutateResource } from '../../tenancy/resourcePermissions';
import apiClient from '../../utils/apiClient';
import CustomFieldInputs, { rawChange } from '../customFields/CustomFieldInputs';

function ModalObjectCard({ onClose, object, onDelete, source, initialEditOpen = false }) {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' or 'layout'
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [avitoPreviewOpen, setAvitoPreviewOpen] = useState(false);

  // Добавляем состояния для модального окна копирования
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const [jsonfiles] = useState(objectStore.jsonfiles);
  /*  logger.log(`🤑 jsonfiles: ${JSON.stringify(jsonfiles)}`) */

  // Создаем ref для связи с дочерним компонентом
  const childRef = useRef(null);

  // Найти соответствующий jsonfile по subcategoryId
  const matchedJsonfile = jsonfiles.find(
    (jsonfile) => jsonfile.type && parseInt(jsonfile.type.id) === parseInt(object.subcategoryId),
  );

  logger.log(`🤑 matchedJsonfile: ${JSON.stringify(matchedJsonfile)}`);

  const [open, setOpen] = useState(initialEditOpen);
  const [customFieldValues, setCustomFieldValues] = useState({});

  useEffect(() => {
    let alive = true;
    apiClient.get(`/api/objects/${object.id}/custom-fields`).then(response => {
      if (!alive) return;
      setCustomFieldValues(Object.fromEntries(response.data.values.map(value => [value.definitionId, {
        definition: { id: value.definitionId, type: value.type },
        raw: value.type === 'SINGLE_SELECT' ? value.optionId : value.value,
      }])));
    }).catch(() => { if (alive) setCustomFieldValues({}); });
    return () => { alive = false; };
  }, [object.id]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleShareUrlGenerate = () => {
    logger.log('handleShareUrlGenerate');

    // Формируем ссылку для шаринга
    const url = `${import.meta.env.VITE_OPEN_APP_URL}/object/${object.uuid}`;

    // Копируем ссылку в буфер обмена
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setSnackbarOpen(true);
        })
        .catch((error) => {
          logger.log(`Ошибка при копировании ссылки: ${error}`);
        });
    }
  };

  // Обработчик выбора текста в поле с ссылкой
  const handleSelectShareUrlText = (event) => {
    event.target.select();
    try {
      // Пробуем скопировать выделенный текст
      document.execCommand('copy');
      setSnackbarOpen(true);
    } catch (err) {
      logger.log('Ошибка копирования текста:', err);
    }
  };

  // Закрытие диалога с ссылкой
  const handleShareLinkDialogClose = () => {
    setShareLinkDialogOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // The server-scoped DTO is the authority for fields present in this view.
  const excludedFields = [
    'description', 'city', 'address', 'price',
    'comment', 'sharedcomment', 'commentforme', 'commentforcolleagues', 'commentforall',
  ];

  const renderFieldValue = (field, value) => {
    if (!field) return null;

    const { controlType, options, multiple } = field;

    if (controlType === 'buttons' || controlType === 'dropdown') {
      // Если значение - массив (множественный выбор)
      if (Array.isArray(value)) {
        const selectedOptions = value
          .map((id) => options.find((option) => option.id === id)?.value)
          .filter(Boolean);
        return selectedOptions.join(', ');
      }
      // Если значение - одиночный выбор
      const selectedOption = options.find((option) => option.id === value);
      return selectedOption?.value || 'Неизвестно';
    }

    // Для других типов полей просто вернуть значение
    return value;
  };

  // Функция для проверки, нужно ли отображать поле комментария
  // Проход по полям jsonfiles и рендер только тех, которые есть в object, за исключением excludedFields
  const renderFields = () => {
    if (!matchedJsonfile) {
      return <p>Нет доступных данных для отображения.</p>;
    }

    return Object.entries(matchedJsonfile).map(([key, field]) => {
      // Пропускаем технические поля, исключенные поля и поле "rooms"
      if (key === 'type' || excludedFields.includes(key) || key === 'rooms') return null;

      // Проверяем, есть ли поле в object или details
      let value = object[key];
      if (value === undefined || value === null) {
        // Если в основном объекте поля нет, ищем в details
        value = object.details?.[key];
      }

      if (value === undefined || value === null) return null;

      // Особое форматирование для полей комментариев
      // Стандартный рендер для остальных полей в виде двух колонок с разделителем
      return (
        <Box key={key} sx={{ marginBottom: 0 }}>
          <div className="flex flex-row items-center justify-between py-4 border-b border-gray-400">
            <Typography sx={{ color: 'text.secondary' }} className="font-medium text-base">
              {field.label}
            </Typography>
            <div className="text-right">{renderFieldValue(field, value)}</div>
          </div>
        </Box>
      );
    });
  };

  const handleOpenConfirm = () => setOpenConfirm(true);
  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleDelete = () => {
    onDelete(object.id); // Передаем `id` объекта в функцию удаления
    setOpenConfirm(false); // Закрываем диалог подтверждения
  };

  const handleTabChange = (event, newTab) => {
    if (newTab !== null) setActiveTab(newTab);
  };

  const handleImageClick = (image) => {
    setFullscreenImage(image);
    setFullscreenOpen(true);
  };

  

  /* Используем данные напрямую из объекта, добавляем ?. для безопасности */
  const photos = object.photos || [];
  const layoutPhotos = object.layoutPhotos || [];

  const images = activeTab === 'photos' ? photos : layoutPhotos;

  // Состояние для отслеживания ошибок загрузки изображений в галерее
  const [imageErrors, setImageErrors] = useState({});

  // Функция для обработки ошибок загрузки изображений
  const handleImageError = (index) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  // Функция обработки нажатия кнопки
  const handleSaveButtonClick = async () => {
    // Вызываем функцию дочернего компонента
    logger.log(`childRef: ${JSON.stringify(childRef)}`);

    if (childRef.current) {
      const isValid = childRef.current.handleSubmit(); // Вызов функции handleSubmit из дочернего компонента и получение результата

      // Закрываем диалог только если валидация прошла успешно
      if (isValid) {
			const changes = Object.entries(customFieldValues).map(([definitionId, value]) => rawChange({ id: definitionId, ...value.definition }, value.raw, value.clear));
			if (changes.length) {
				try { await apiClient.patch(`/api/objects/${object.id}/custom-fields`, { changes }); }
				catch (error) { logger.error('Custom field save failed', error); return; }
			}
        handleClose();
      }
    }
  };

  return (
    <>
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
        <div onClick={() => logger.log(JSON.stringify(object, null, 2))}>% ModalObjectCard %</div>
      )}

      <div className="flex flex-row justify-end space-x-2">
        {source !== 'request' && canMutateResource(object) && (
          <Button onClick={handleOpenConfirm}>
            <LuTrash2 className="text-xl" />
          </Button>
        )}


        {canDistributeObject(object) && (
          <Button onClick={handleShareUrlGenerate}>
            <IoShareSocialOutline className="text-xl" />
          </Button>
        )}

        {source != 'request' && canMutateResource(object) && (
          <Button onClick={handleOpen}>
            <AiOutlineEdit className="text-xl" />
          </Button>
        )}
        {canMutateResource(object) && (
          <Button onClick={() => setAvitoPreviewOpen(true)}>Предпросмотр Авито</Button>
        )}
        <Button onClick={onClose}>
          <IoClose className="text-xl" />
        </Button>
      </div>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          mb: 1,
          mt: 1,
          fontSize: '1.0rem',
          letterSpacing: '0.03em',
        }}>
        {getObjectFullInfo(object, objectStore.categories)}
      </Typography>

      <div className="mb-5 mt-3">
        <RenderSelectedTags tags={object.details?.tags} />
      </div>
      {scopedObjectComments(object).map(comment => (
        <Box key={comment.key} sx={{ marginBottom: 3, marginTop: 2 }}>
          <div className="flex flex-col border-l-4 border-gray-300 pl-3 py-2">
            <p className="font-bold text-base mb-1">{comment.label}:</p>
            <p className="pl-2" style={{ whiteSpace: 'pre-line' }}>{comment.value}</p>
          </div>
        </Box>
      ))}
      <ObjectDistributionControls object={object} />

      {/* Проверка на наличие данных перед рендером кнопок */}
      {(photos.length > 0 || layoutPhotos.length > 0) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginY: 2 }}>
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={handleTabChange}
			aria-label="Выбор фотографий или планировок"
            size="small">
            {photos.length > 0 && (
              <ToggleButton value="photos" size="small">
                Фото
              </ToggleButton>
            )}
            {layoutPhotos.length > 0 && <ToggleButton value="layout">Планировка</ToggleButton>}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Галерея Swiper */}
      {images.length > 0 && (
        <Box sx={{ marginY: 2 }}>
          {/* Основная галерея */}
          <Swiper
            modules={[Navigation, Pagination, Thumbs]}
            spaceBetween={10}
            slidesPerView={1}
            navigation={{ hideOnClick: true }}
            pagination={{ clickable: true }}
            thumbs={{ swiper: thumbsSwiper }}>
            {images.map((item, index) => (
              <SwiperSlide key={item.uuid || index} onClick={() => handleImageClick(item.imageUrl)}>
                {/* Проверяем наличие ошибки для данного индекса */}
                {imageErrors[index] ? (
                  <Box
                    sx={{
                      height: '100%', // Адаптируем высоту плейсхолдера
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.grey[200], // Цвет фона плейсхолдера
                      borderRadius: '8px',
                    }}>
                    <HideImageIcon sx={{ fontSize: 60, color: theme.palette.grey[500] }} />
                  </Box>
                ) : (
                  <img
                    src={item.imageUrl} /* Используем imageUrl */
					alt={`Фотография ${index + 1}`}
                    className="w-full rounded-lg cursor-pointer object-cover h-[300px] sm:h-[350px] md:h-[400px] lg:h-[500px] xl:h-[600px]"
                    onError={() => handleImageError(index)} /* Добавляем onError */
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Миниатюры */}
          <Swiper
            onSwiper={setThumbsSwiper}
            modules={[Thumbs]}
            spaceBetween={10}
            slidesPerView={6}
            watchSlidesProgress>
            {images.map((item, index) => (
              <SwiperSlide key={`thumb-${item.uuid || index}`}>
                {/* Можно добавить плейсхолдер и для миниатюр, если нужно */}
                {imageErrors[index] ? (
                  <Box
                    sx={{
                      height: '100%', // Высота плейсхолдера для миниатюры
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.grey[200],
                      borderRadius: '4px', // Меньше скругление для миниатюр
                      mt: 2, // Добавляем отступ сверху как у img
                    }}>
                    <HideImageIcon sx={{ fontSize: 30, color: theme.palette.grey[500] }} />
                  </Box>
                ) : (
                  <img
                    src={item.imageUrl} /* Используем imageUrl */
					alt={`Миниатюра фотографии ${index + 1}`}
                    className="mt-4 w-full rounded-md cursor-pointer object-cover h-[50px] sm:h-[60px] md:h-[70px] lg:h-[80px] xl:h-[100px]"
                    onError={() => handleImageError(index)} /* Добавляем onError */
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      )}

      <div className="flex mt-10 font-semibold text-3xl">{object.renderTitle}</div>

      <div className="font-medium text-xl mt-6"> {object.address} </div>
      <div className="font-semibold text-3xl mt-6">
        {getPrice(object.details).value} {getPrice(object.details).suffix}
      </div>
      <div className="mt-6">
        <div className="font-semibold text-xl">Описание</div>
        <div className="mt-2" style={{ whiteSpace: 'pre-line' }}>
          {object.description}
        </div>
      </div>
      {/* Динамический рендер параметров объекта */}
      <div className="mb-32 mt-8">{renderFields()}</div>

      <Dialog open={openConfirm} onClose={handleCloseConfirm}>
        <DialogTitle>
          {i18nStore.translations.objectCardRu.deleteObject}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            
            {i18nStore.translations.objectCardRu.deleteObjectConfirm}{' '}
            <p className="mt-5"> {object.renderTitle}</p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            {i18nStore.translations.buttons.cancel}
          </Button>
          <Button onClick={handleDelete} color="error">
            {i18nStore.translations.buttons.yes}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Полноэкранный просмотр */}
      <Dialog full Screen open={fullscreenOpen} onClose={() => setFullscreenOpen(false)}>
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <IconButton
            sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}
            onClick={() => setFullscreenOpen(false)}>
            <CloseIcon />
          </IconButton>
          {fullscreenImage /* Проверка что fullscreenImage не null */ ? (
            <img
              src={fullscreenImage}
				alt="Полноэкранное изображение"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={(e) => {
                e.target.src = '/placeholder.svg';
              }} // Простой фоллбэк для полноэкранного режима
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.grey[200],
              }}>
              <HideImageIcon sx={{ fontSize: 100, color: theme.palette.grey[500] }} />
            </Box>
          )}
        </Box>
      </Dialog>

      <Dialog open={open} onClose={handleClose} fullScreen={isMobile} maxWidth="md" fullWidth>
        <DialogTitle>
          Редактировать объект
          {isMobile && (
            <IconButton
				aria-label="Закрыть"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            padding: isMobile ? '12px 16px' : '20px 24px',
            overflowY: 'auto',
            height: isMobile ? 'calc(100% - 130px)' : 'auto',
            
          }}>
          <DynamicFieldsEdit object={object} fields={matchedJsonfile} ref={childRef} />
		  <CustomFieldInputs
			values={Object.fromEntries(Object.entries(customFieldValues).map(([id, value]) => [id, value.raw]))}
			onChange={(definition, raw, clear) => setCustomFieldValues(current => ({ ...current, [definition.id]: { definition, raw, clear } }))}
		  />
        </DialogContent>
        <DialogActions
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            padding: isMobile ? '10px 16px' : '10px 24px',
            boxShadow: '0px -2px 10px rgba(0,0,0,0.2)',
            position: isMobile ? 'fixed' : 'relative',
            bottom: isMobile ? 0 : 'auto',
            backgroundColor: theme.palette.background.paper,
          }}>
          <Box
            style={{
              display: 'flex',
              justifyContent: isMobile ? 'space-between' : 'flex-end',
              width: isMobile ? '100%' : 'auto',
              gap: '10px',
            }}>
            <Button onClick={handleClose}>Отмена</Button>
            <Button variant="contained" onClick={handleSaveButtonClick}>
              Сохранить
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={shareLinkDialogOpen}
        onClose={handleShareLinkDialogClose}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Ссылка на объект</DialogTitle>
        <DialogContent
       
        >
          <DialogContentText sx={{ mb: 2 }}>Ссылка на объект:</DialogContentText>
          <TextField
            fullWidth
            value={shareUrl}
            onClick={handleSelectShareUrlText}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          {/*      <Typography variant="caption" color="text.secondary">
            Нажмите на текст выше, чтобы выделить ссылку, затем скопируйте её с помощью кнопки вашего устройства или меню копирования
          </Typography> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShareLinkDialogClose}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Ссылка скопирована в буфер обмена
        </Alert>
      </Snackbar>

      <AvitoPreviewDialog
        open={avitoPreviewOpen}
        onClose={() => setAvitoPreviewOpen(false)}
        objectId={object.id}
      />
    </>
  );
}

export default ModalObjectCard;
